import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Autocomplete,
  ResourceList,
  ResourceItem,
  Text,
  Select,
  BlockStack,
  InlineStack,
  Button,
  Card,
  Divider,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetch } from "containers/products/products.service";
import { fetch as fetchProductGroups } from "services/product-groups.service";
import { APP_CONFIG } from "config/app-config";
import debounce from "lodash/debounce";
import { Product, ProductGroup, ProductGroupItem } from "types/Product";
import { ProductLineFields, RemoveLineButton } from "./ProductLineFields";

const DEBOUNCE_DELAY = 300;

interface ProductsAndGroupsSelectProps {
  onChange: (data: { products: Product[]; productGroups: ProductGroupItem[] }) => void;
  initialProducts?: Product[];
  initialProductGroups?: ProductGroupItem[];
  disabled?: boolean;
  error?: string;
  isRequired?: boolean;
  enableGroupOption?: boolean;
}

const formatProductLabel = (product: Product) => {
  const codes = [product.sku, product.supplierSku].filter(
    (value): value is string => Boolean(value)
  );
  const codesSuffix = codes.length ? ` [${codes.join(" / ")}]` : "";
  return `${product.title}${codesSuffix}`;
};

const formatProductGroupLabel = (group: ProductGroup | null | undefined): string => {
  if (!group) return "";
  const code = group.code || "";
  const name = group.name || group.id || "";
  if (code && name) {
    return `${code} - ${name}`;
  }
  return name || code || "";
};

const ProductsAndGroupsSelect: React.FC<ProductsAndGroupsSelectProps> = ({
  onChange,
  initialProducts = [],
  initialProductGroups = [],
  disabled = false,
  isRequired = false,
  enableGroupOption = true,
  error,
}) => {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>(initialProducts);
  const [selectedProductGroups, setSelectedProductGroups] = useState<ProductGroupItem[]>(
    initialProductGroups || []
  );
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [productGroupSearchResults, setProductGroupSearchResults] = useState<ProductGroup[]>([]);
  const [query, setQuery] = useState("");
  const [defaultList, setDefaultList] = useState<Product[]>([]);
  const [productGroupDefaultList, setProductGroupDefaultList] = useState<ProductGroup[]>([]);
  const defaultListRef = useRef<Product[]>([]);
  const hasInitializedFromProps = useRef(false);
  const previousInitialProductsRef = useRef<string>("");
  const previousInitialProductGroupsRef = useRef<string>("");
  const { companyId } = useParams();
  const { t } = useTranslation();

  // Sync state when props change (for editing existing files)
  useEffect(() => {
    const currentProductsStr = JSON.stringify(initialProducts);
    const currentGroupsStr = JSON.stringify(initialProductGroups || []);
    
    if (!hasInitializedFromProps.current) {
      // First time: initialize from props
      setSelectedProducts(initialProducts);
      setSelectedProductGroups(initialProductGroups || []);
      previousInitialProductsRef.current = currentProductsStr;
      previousInitialProductGroupsRef.current = currentGroupsStr;
      hasInitializedFromProps.current = true;
      return;
    }
    
    // Subsequent updates: only update if props have actually changed
    if (
      previousInitialProductsRef.current !== currentProductsStr ||
      previousInitialProductGroupsRef.current !== currentGroupsStr
    ) {
      setSelectedProducts(initialProducts);
      setSelectedProductGroups(initialProductGroups || []);
      previousInitialProductsRef.current = currentProductsStr;
      previousInitialProductGroupsRef.current = currentGroupsStr;
    }
  }, [initialProducts, initialProductGroups]);

  // Notify parent of changes (but skip until we've initialized from props)
  useEffect(() => {
    if (!hasInitializedFromProps.current) {
      return;
    }
    // Validate products have quantity and price
    const productsValid = selectedProducts.length === 0 || selectedProducts.every(
      (product) => product.quantity && product.price
    );
    // Product groups are always valid (they have default quantity/price)
    if (productsValid) {
      onChange({ products: selectedProducts, productGroups: selectedProductGroups });
    }
  }, [selectedProducts, selectedProductGroups, onChange]);

  // Fetch initial products
  useEffect(() => {
    const fetchInitialProducts = async () => {
      if (companyId) {
        const results: Product[] | undefined = await searchProducts(companyId);
        const products = results || [];
        setDefaultList(products);
        defaultListRef.current = products;
        setSearchResults(products);
      }
    };
    fetchInitialProducts();
  }, [companyId]);

  // Fetch initial product groups
  useEffect(() => {
    const fetchInitialData = async () => {
      if (companyId && enableGroupOption) {
        const productGroups = await searchProductGroups(companyId);
        setProductGroupDefaultList(productGroups || []);
        setProductGroupSearchResults(productGroups || []);
      }
    };
    fetchInitialData();
  }, [companyId, enableGroupOption]);

  const handleSearchChange = useCallback(
    debounce(async (value: string) => {
      if (value.length > 0 && companyId) {
        const results: Product[] | undefined = await searchProducts(
          companyId,
          value
        );
        // Use ref to get latest defaultList value
        setSearchResults(results || defaultListRef.current);
      } else {
        // Use ref to get latest defaultList value
        setSearchResults(defaultListRef.current);
      }
    }, DEBOUNCE_DELAY),
    [companyId]
  );

  const onSearchInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (value === "") {
        // Use ref to get latest defaultList value
        setSearchResults(defaultListRef.current);
      } else {
        handleSearchChange(value);
      }
    },
    [handleSearchChange]
  );

  // Product handlers
  const handleAddProduct = useCallback((product: Product) => {
    setSelectedProducts((prev) => {
      if (prev.some((p) => p.product === product.product)) {
        return prev;
      }
      return [...prev, { ...product, quantity: 1, price: product.price }];
    });
  }, []);

  const handleRemoveProduct = useCallback((productId: string) => {
    setSelectedProducts((prev) =>
      prev.filter((product) => product.product !== productId)
    );
  }, []);

  const handleQuantityChange = useCallback(
    (productId: string, quantity: any) => {
      setSelectedProducts((prev) =>
        prev.map((product) =>
          product.product === productId ? { ...product, quantity } : product
        )
      );
    },
    []
  );

  const handlePriceChange = useCallback((productId: string, price: any) => {
    setSelectedProducts((prev) =>
      prev.map((product) =>
        product.product === productId ? { ...product, price } : product
      )
    );
  }, []);

  const handleNotesChange = useCallback(
    (productId: string, notes: string | "") => {
      setSelectedProducts((prev) =>
        prev.map((product) =>
          product.product === productId ? { ...product, notes } : product
        )
      );
    },
    []
  );

  // Product group handlers
  const handleAddProductGroup = useCallback(
    (group: ProductGroup) => {
      setSelectedProductGroups((prev) => {
        if (!prev.some((pg) => pg.productGroup === group.id)) {
          const newGroup: ProductGroupItem = {
            productGroup: group.id,
            quantity: 1,
            price: 0,
            notes: "",
          };
          return [...prev, newGroup];
        }
        return prev;
      });
    },
    []
  );

  const handleRemoveProductGroup = useCallback(
    (productGroupId: string) => {
      setSelectedProductGroups((prev) =>
        prev.filter((pg) => pg.productGroup !== productGroupId)
      );
    },
    []
  );

  const handleProductGroupQuantityChange = useCallback(
    (productGroupId: string, quantity: any) => {
      setSelectedProductGroups((prev) =>
        prev.map((pg) =>
          pg.productGroup === productGroupId ? { ...pg, quantity } : pg
        )
      );
    },
    []
  );

  const handleProductGroupPriceChange = useCallback(
    (productGroupId: string, price: any) => {
      setSelectedProductGroups((prev) =>
        prev.map((pg) =>
          pg.productGroup === productGroupId ? { ...pg, price } : pg
        )
      );
    },
    []
  );

  const handleProductGroupNotesChange = useCallback(
    (productGroupId: string, notes: string) => {
      setSelectedProductGroups((prev) =>
        prev.map((pg) =>
          pg.productGroup === productGroupId ? { ...pg, notes } : pg
        )
      );
    },
    []
  );

  const handleProductGroupSelectChange = useCallback(
    (value: string) => {
      if (value && value !== "") {
        const group =
          productGroupSearchResults.find((g) => g.id === value) ||
          productGroupDefaultList.find((g) => g.id === value);
        if (group) {
          handleAddProductGroup(group);
        }
      }
    },
    [productGroupSearchResults, productGroupDefaultList, handleAddProductGroup]
  );

  const renderProductItem = useCallback(
    (product: Product) => {
      const quantity = product.quantity.toString();
      const price = product.price.toString();
      return (
        <ResourceItem
          key={product.product}
          id={product.product}
          onClick={() => {}}
          accessibilityLabel={`View details for ${product.title}`}
        >
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <Text as="p" fontWeight="bold">
              {formatProductLabel(product)}
            </Text>
            <RemoveLineButton
              onRemove={() => handleRemoveProduct(product.product)}
            />
          </InlineStack>
          <ProductLineFields
            quantity={quantity}
            price={price}
            notes={product.notes || ""}
            onQuantityChange={(value) =>
              handleQuantityChange(product.product, value)
            }
            onPriceChange={(value) => handlePriceChange(product.product, value)}
            onNotesChange={(value) => handleNotesChange(product.product, value)}
          />
        </ResourceItem>
      );
    },
    [handleRemoveProduct, handleQuantityChange, handlePriceChange, handleNotesChange]
  );

  const hasSelection = selectedProducts.length > 0 || selectedProductGroups.length > 0;
  const showError = isRequired && !hasSelection;
  // Use error prop if provided, otherwise use showError logic
  const displayError = error || (showError ? t("at_least_one_product_or_product_group_required") : undefined);

  return (
    <BlockStack gap="400">
      <Autocomplete
        options={searchResults.map((product) => ({
          value: product.product,
          label: formatProductLabel(product),
        }))}
        selected={[]}
        onSelect={(selected) => {
          const product = searchResults.find((p) => p.product === selected[0]);
          if (product) handleAddProduct(product);
        }}
        textField={
          <Autocomplete.TextField
            label={t("search_products")}
            value={query}
            autoComplete="off"
            onChange={onSearchInputChange}
            requiredIndicator={isRequired}
            error={displayError}
          />
        }
      />
      {selectedProducts.length > 0 && (
        <ResourceList items={selectedProducts} renderItem={renderProductItem} />
      )}

      {enableGroupOption && (
        <>
          {selectedProducts.length > 0 && <Divider />}
          <Select
            label={t("select_product_group")}
            options={[
              { label: t("select_product_group"), value: "" },
              ...productGroupSearchResults.map((group) => ({
                label: formatProductGroupLabel(group),
                value: group.id,
              })),
            ]}
            value=""
            onChange={handleProductGroupSelectChange}
          />
          {selectedProductGroups.length > 0 && (
            <BlockStack gap="400">
              <Text as="p" variant="bodyMd" fontWeight="medium">
                {t("selected_product_groups")}:
              </Text>
              {selectedProductGroups.map((pgItem) => {
                const group =
                  productGroupSearchResults.find((g) => g.id === pgItem.productGroup) ||
                  productGroupDefaultList.find((g) => g.id === pgItem.productGroup);
                const quantity = pgItem.quantity.toString();
                const price = pgItem.price.toString();

                return (
                  <Card key={pgItem.productGroup}>
                    <BlockStack gap="300">
                      <InlineStack align="space-between">
                        <Text as="p" fontWeight="bold">
                          {group ? formatProductGroupLabel(group) : pgItem.productGroup}
                        </Text>
                        <Button
                          variant="plain"
                          size="slim"
                          onClick={() => handleRemoveProductGroup(pgItem.productGroup)}
                          accessibilityLabel={t("remove")}
                        >
                          {t("remove")}
                        </Button>
                      </InlineStack>
                      <ProductLineFields
                        quantity={quantity}
                        price={price}
                        notes={pgItem.notes || ""}
                        onQuantityChange={(value) =>
                          handleProductGroupQuantityChange(pgItem.productGroup, value)
                        }
                        onPriceChange={(value) =>
                          handleProductGroupPriceChange(pgItem.productGroup, value)
                        }
                        onNotesChange={(value) =>
                          handleProductGroupNotesChange(pgItem.productGroup, value)
                        }
                      />
                    </BlockStack>
                  </Card>
                );
              })}
            </BlockStack>
          )}
        </>
      )}
    </BlockStack>
  );
};

// Simulated AJAX search function for products
const searchProducts = async (
  companyId: string,
  query?: string
): Promise<Product[] | undefined> => {
  const filter = { textSearch: query };
  try {
    const { productsCollection } = await fetch(companyId, filter);
    return productsCollection
      .filter((product: any) => {
        // hide products which code starts with "G" for UHB service company
        if (APP_CONFIG.isUhbGroup && companyId === "8c1d50dc-85b8-48df-be85-fa680d698f9f") {
          // Check if sku exists and is a string before calling startsWith
          return !(product.sku && typeof product.sku === "string" && product.sku.startsWith("G"));
        }
        return true;
      })
      .map((product: any) => ({
        product: product.id,
        value: product.id,
        title: product.title,
        quantity: 0,
        sku: product.sku,
        supplierSku: product.supplier_sku,
        price: product.price,
      }));
  } catch (e) {
    return undefined;
  }
};

// Simulated AJAX search function for product groups
const searchProductGroups = async (
  companyId: string,
  query?: string
): Promise<ProductGroup[] | undefined> => {
  const filter = { textSearch: query };
  try {
    const { productGroupsCollection } = await fetchProductGroups(companyId, filter);
    return productGroupsCollection.map((group: any) => ({
      id: group.id,
      name: group.name,
      code: group.code,
      description: group.description,
      products: group.products || [],
    }));
  } catch (e) {
    return undefined;
  }
};

export default ProductsAndGroupsSelect;

